import React from 'react';
import { Input } from '../../../../components/ui/input';
import { AiButton, Field, StreamPreview, inputCls } from '../shared';

export const validate = (wf) => {
  if (!wf.topic?.trim()) return 'Enter a blog topic.';
  if (!wf.data?.keywords?.primary_keyword?.trim()) return 'Pick a primary keyword.';
  return null;
};

const INTENT_COLORS = {
  informational: 'bg-sky-50 text-sky-700',
  commercial: 'bg-amber-50 text-amber-700',
  transactional: 'bg-emerald-50 text-emerald-700',
  navigational: 'bg-[var(--bg-alt)] text-ink-3',
};

const StepKeywords = ({ wf, setData, setTopic, runAi, ai }) => {
  const d = wf.data?.keywords || {};
  const list = d.ai?.keywords || [];
  const secondary = d.secondary_keywords || [];
  const running = ai.running && ai.action === 'keywords';

  const generate = async () => {
    if (!wf.topic?.trim()) return;
    const res = await runAi('keywords');
    if (res) setData('keywords', { ai: res, primary_keyword: d.primary_keyword || res.recommended_primary || '' });
  };

  const toggleSecondary = (k) =>
    setData('keywords', { secondary_keywords: secondary.includes(k) ? secondary.filter((x) => x !== k) : [...secondary, k] });

  return (
    <div className="space-y-6">
      <Field label="Blog topic" required testId="kw-topic-field">
        <Input value={wf.topic} onChange={(e) => setTopic(e.target.value)} className={`${inputCls} h-12`} placeholder="e.g. How to measure executive briefing ROI" data-testid="kw-topic-input" />
      </Field>
      <AiButton onClick={generate} running={running} label="Generate with AI" runningLabel="Researching keywords…" testId="kw-generate-btn" disabled={!wf.topic?.trim()} />
      {running && <StreamPreview text={ai.text} label="Researching keywords…" />}

      {list.length > 0 && !running && (
        <div className="rounded-2xl border border-[var(--line-2)] overflow-hidden" data-testid="kw-results">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-alt)] text-ink-3 text-[11px] uppercase tracking-widest">
              <tr>
                <th className="text-left px-4 py-2.5">Primary</th>
                <th className="text-left px-4 py-2.5">Keyword</th>
                <th className="text-right px-4 py-2.5">Searches / mo*</th>
                <th className="text-right px-4 py-2.5">Difficulty</th>
                <th className="text-left px-4 py-2.5">Intent</th>
                <th className="text-left px-4 py-2.5">Secondary</th>
              </tr>
            </thead>
            <tbody>
              {list.map((k) => (
                <tr key={k.keyword} className={`border-t border-[var(--line-2)] ${d.primary_keyword === k.keyword ? 'bg-[var(--bg-alt)]' : ''}`}>
                  <td className="px-4 py-2.5">
                    <input type="radio" name="primary" checked={d.primary_keyword === k.keyword} onChange={() => setData('keywords', { primary_keyword: k.keyword })} data-testid={`kw-primary-${k.keyword.replace(/\s+/g, '-')}`} />
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="text-ink font-medium">{k.keyword}</div>
                    <div className="text-[11px] text-ink-3">{k.rationale}</div>
                  </td>
                  <td className="px-4 py-2.5 text-right text-ink-2">{Number(k.monthly_searches || 0).toLocaleString()}</td>
                  <td className="px-4 py-2.5 text-right">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] ${k.difficulty > 60 ? 'bg-red-50 text-red-700' : k.difficulty > 35 ? 'bg-amber-50 text-amber-700' : 'bg-emerald-50 text-emerald-700'}`}>{k.difficulty}</span>
                  </td>
                  <td className="px-4 py-2.5"><span className={`px-2 py-0.5 rounded-full text-[11px] ${INTENT_COLORS[k.intent] || INTENT_COLORS.navigational}`}>{k.intent}</span></td>
                  <td className="px-4 py-2.5">
                    <input type="checkbox" checked={secondary.includes(k.keyword)} disabled={d.primary_keyword === k.keyword} onChange={() => toggleSecondary(k.keyword)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2 text-[10px] text-ink-3 bg-[var(--bg-alt)]">* Volume and difficulty are AI estimates, not live search-engine data.</div>
        </div>
      )}

      <Field label="Chosen primary keyword" required testId="kw-primary-field">
        <Input value={d.primary_keyword || ''} onChange={(e) => setData('keywords', { primary_keyword: e.target.value })} className={`${inputCls} h-12`} placeholder="Pick the primary keyword from the research above" data-testid="kw-primary-input" />
      </Field>
      <Field label="Secondary keywords" hint="Comma separated. Tick the Secondary column above or type your own." testId="kw-secondary-field">
        <Input value={secondary.join(', ')} onChange={(e) => setData('keywords', { secondary_keywords: e.target.value.split(',').map((s) => s.trim()).filter(Boolean) })} className={inputCls} data-testid="kw-secondary-input" />
      </Field>
    </div>
  );
};

export default StepKeywords;
